'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Calendar, Users, ChevronLeft, ChevronRight, ChevronDown as LucideChevronDown, ShieldCheck, Star, CheckCircle2, Loader2 } from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { CmsRoom } from '@/lib/cms';
async function fetchSandyAvailableIds(checkIn: string, checkOut: string): Promise<Set<number>> {
  const res = await fetch('/api/sandy/availability', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ checkIn, checkOut }),
  });
  if (!res.ok) throw new Error(`availability check failed: ${res.status}`);
  const data = await res.json();
  return new Set<number>((data.availableRoomTypeIds ?? []).map(Number));
}
import { buildSignedBookingUrl } from '@/lib/booking-navigate';
import { ButtonSpinner } from '@/components/ButtonSpinner';

const { colors, fonts } = siteTheme;
const navy = '#0D2F44';

/* ── Capacity helpers ── */
function roomMaxCapacity(r: CmsRoom) {
  return { maxA: Math.max(1, r.max_adults ?? 2), maxC: Math.max(0, r.max_children ?? 1) };
}
function neededRooms(adults: number, children: number, maxA: number, maxC: number): number {
  const fromA = Math.ceil(adults / maxA);
  const fromC = maxC > 0 && children > 0 ? Math.ceil(children / maxC) : 0;
  return Math.max(1, fromA, fromC);
}

/* ── Shared helpers (same as Type1 / Type2) ── */
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mo','Tu','We','Th','Fr','Sa','Su'];

function useClickOutside(ref: React.RefObject<HTMLElement | null>, cb: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    function h(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) cb(); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, cb, enabled]);
}

function toISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function isoToLocal(iso: string) { return iso ? new Date(iso + 'T00:00:00') : null; }
function fmtDisplay(iso: string) {
  const d = isoToLocal(iso);
  return d ? d.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : 'Select date';
}
function calcNights(cin: string, cout: string) {
  if (!cin || !cout) return 0;
  return Math.max(0, Math.ceil(
    (new Date(cout+'T00:00:00').getTime() - new Date(cin+'T00:00:00').getTime()) / 86_400_000
  ));
}

/* ── Icons ── */
const CalIcon    = () => <Calendar size={14} color={navy} style={{ flexShrink: 0, opacity: 0.6 }} />;
const PeopleIcon = () => <Users size={16} color={navy} style={{ flexShrink: 0, opacity: 0.6 }} />;
const ArrowL     = () => <ChevronLeft size={12} color={navy} strokeWidth={1.5} />;
const ArrowR     = () => <ChevronRight size={12} color={navy} strokeWidth={1.5} />;
const ChevDown   = ({ open }: { open: boolean }) => (
  <LucideChevronDown size={10} color="rgba(13,47,68,0.45)" strokeWidth={1.4}
    style={{ pointerEvents: 'none', flexShrink: 0, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
);

/* ── Shared field style ── */
const fieldStyle: React.CSSProperties = {
  width:'100%', backgroundColor:'rgba(255,255,255,0.65)',
  border:'1px solid rgba(13,47,68,0.13)', borderRadius:'0.55rem',
  color:navy, fontFamily:fonts.sans, outline:'none', cursor:'pointer',
  display:'flex', alignItems:'center', gap:'0.55rem',
  padding:'0.9rem 1rem', fontSize:'1.2rem',
};
const panelBase: React.CSSProperties = {
  position:'absolute', top:'calc(100% + 0.4rem)', zIndex:300,
  minWidth:'28rem', maxWidth:'calc(100vw - 2rem)',
  background:'rgba(250,249,247,0.99)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)',
  border:'1px solid rgba(13,47,68,0.12)', borderRadius:'0.8rem',
  boxShadow:'0 12px 40px rgba(0,0,0,0.15)',
};
function panelStyle(alignRight: boolean): React.CSSProperties {
  return alignRight ? { ...panelBase, right:0, left:'auto' } : { ...panelBase, left:0, right:'auto' };
}
const labelStyle: React.CSSProperties = {
  fontFamily:fonts.sans, fontSize:'1rem', fontWeight:600,
  letterSpacing:'0.11em', textTransform:'uppercase',
  color:'rgba(13,47,68,0.5)', marginBottom:'0.5rem',
  display:'flex', alignItems:'center', gap:'0.35rem',
};

/* ── +/− counter inline ── */
function Counter({ value, onChange, min=0, max=10 }: {
  value: number; onChange:(v:number)=>void; min?:number; max?:number;
}) {
  const btn = (dis: boolean): React.CSSProperties => ({
    width:'2.4rem', height:'2.4rem', borderRadius:'50%',
    border:`1px solid rgba(13,47,68,${dis?'0.1':'0.2'})`,
    background:'transparent', color:navy, fontSize:'1.6rem', lineHeight:1,
    display:'flex', alignItems:'center', justifyContent:'center',
    cursor:dis?'default':'pointer', opacity:dis?0.3:1, fontFamily:fonts.sans, fontWeight:300,
  });
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'0.8rem' }}>
      <button type="button" onClick={()=>onChange(Math.max(min,value-1))} style={btn(value<=min)}>−</button>
      <span style={{ fontFamily:fonts.sans, fontSize:'1.35rem', fontWeight:600, color:navy, minWidth:'1.6rem', textAlign:'center' }}>{value}</span>
      <button type="button" onClick={()=>onChange(Math.min(max,value+1))} style={btn(value>=max)}>+</button>
    </div>
  );
}

/* ── Guest dropdown (adults + children) ── */
function GuestsDropdown({ adults, children, onAdults, onChildren, maxAdults, maxChildren, roomCount }: {
  adults:number; children:number; onAdults:(v:number)=>void; onChildren:(v:number)=>void;
  maxAdults: number; maxChildren: number; roomCount: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  const summary = children > 0
    ? `${adults} Adult${adults!==1?'s':''} · ${children} Child${children!==1?'ren':''}`
    : `${adults} Adult${adults!==1?'s':''}`;
  const capA = roomCount * maxAdults;
  const capC = roomCount * maxChildren;
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={()=>setOpen(v=>!v)} style={fieldStyle}>
        <PeopleIcon />
        <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{summary}</span>
        <ChevDown open={open} />
      </button>
      {open && (
        <div style={{ ...panelStyle(false), padding:'0.5rem 1.4rem 1.2rem' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.7rem 0' }}>
            <div>
              <p style={{ fontFamily:fonts.sans, fontSize:'1.2rem', fontWeight:500, color:navy }}>Adults</p>
            </div>
            <Counter value={adults} onChange={onAdults} min={1} max={capA} />
          </div>
          <div style={{ height:'1px', background:'rgba(13,47,68,0.07)' }} />
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.7rem 0' }}>
            <div>
              <p style={{ fontFamily:fonts.sans, fontSize:'1.2rem', fontWeight:500, color:navy }}>Children</p>
              <p style={{ fontFamily:fonts.sans, fontSize:'1rem', color:'rgba(13,47,68,0.45)', marginTop:'0.1rem' }}>0 – 17 Years Old</p>
            </div>
            <Counter value={children} onChange={onChildren} max={capC} />
          </div>
          <p style={{ fontFamily:fonts.sans, fontSize:'1.05rem', color:'rgba(13,47,68,0.4)', textAlign:'center', padding:'0.25rem 0 0.5rem' }}>
            Max {maxAdults} adult{maxAdults!==1?'s':''} · {maxChildren} child{maxChildren!==1?'ren':''} per room
          </p>
          <button type="button" onClick={()=>setOpen(false)} style={{
            marginTop:'0.4rem', width:'100%', padding:'0.8rem',
            backgroundColor:colors.navyDeep, color:'#fff', border:'none', borderRadius:'0.5rem',
            fontFamily:fonts.sans, fontSize:'1.15rem', fontWeight:500,
            letterSpacing:'0.12em', textTransform:'uppercase', cursor:'pointer',
          }}>Done</button>
        </div>
      )}
    </div>
  );
}

/* ── Calendar dropdown ── */
function DateField({ value, onChange, min, label, icon, alignRight = false }: {
  value:string; onChange:(iso:string)=>void; min?:string; label:string; icon:React.ReactNode; alignRight?:boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  const selDate   = isoToLocal(value);
  const todayDate = new Date(); todayDate.setHours(0,0,0,0);
  const minDate   = min ? isoToLocal(min) : null;

  const [vy, setVy] = useState(selDate?.getFullYear() ?? todayDate.getFullYear());
  const [vm, setVm] = useState(selDate?.getMonth()    ?? todayDate.getMonth());

  useEffect(() => {
    const d = isoToLocal(value);
    if (d) { setVy(d.getFullYear()); setVm(d.getMonth()); }
  }, [value]);

  const firstDay = new Date(vy, vm, 1);
  const daysInMonth = new Date(vy, vm+1, 0).getDate();
  const startOff = (firstDay.getDay()+6)%7;
  const cells: (number|null)[] = [...Array(startOff).fill(null), ...Array.from({length:daysInMonth},(_,i)=>i+1)];
  while (cells.length%7) cells.push(null);

  const isDis = (d:number) => !!(minDate && new Date(vy,vm,d) < minDate);
  const isSel = (d:number) => !!(selDate && selDate.getFullYear()===vy && selDate.getMonth()===vm && selDate.getDate()===d);
  const isTod = (d:number) => todayDate.getFullYear()===vy && todayDate.getMonth()===vm && todayDate.getDate()===d;

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <p style={labelStyle}>{icon}{label}</p>
      <button type="button" onClick={()=>setOpen(v=>!v)} style={fieldStyle}>
        <CalIcon />
        <span style={{ flex:1, whiteSpace:'nowrap' }}>{fmtDisplay(value)}</span>
      </button>
      {open && (
        <div style={{ ...panelStyle(alignRight), padding:'1.4rem 1.4rem 1.2rem' }}>
          {/* Month nav */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.9rem' }}>
            <button type="button" onClick={()=>vm===0?(setVm(11),setVy(y=>y-1)):setVm(m=>m-1)}
              style={{ background:'none', border:'none', cursor:'pointer', padding:'0.4rem', display:'flex', borderRadius:'50%' }}>
              <ArrowL />
            </button>
            <span style={{ fontFamily:fonts.serif, fontWeight:300, fontSize:'1.4rem', color:navy }}>
              {MONTHS[vm]}, {vy}
            </span>
            <button type="button" onClick={()=>vm===11?(setVm(0),setVy(y=>y+1)):setVm(m=>m+1)}
              style={{ background:'none', border:'none', cursor:'pointer', padding:'0.4rem', display:'flex', borderRadius:'50%' }}>
              <ArrowR />
            </button>
          </div>
          {/* Day headers */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:'0.25rem' }}>
            {DAYS.map(d=>(
              <div key={d} style={{ fontFamily:fonts.sans, fontSize:'1rem', fontWeight:600, color:'rgba(13,47,68,0.38)', textAlign:'center', padding:'0.15rem 0', letterSpacing:'0.04em' }}>{d}</div>
            ))}
          </div>
          {/* Days */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'0.2rem' }}>
            {cells.map((d,i) => {
              if (d===null) return <div key={i}/>;
              const dis=isDis(d), sel=isSel(d), tod=isTod(d);
              return (
                <button key={i} type="button"
                  onClick={()=>{ if(!dis){ onChange(toISO(new Date(vy,vm,d))); setOpen(false); } }}
                  style={{
                    height:'3.2rem', borderRadius:'50%',
                    border: tod&&!sel ? `1px solid rgba(13,47,68,0.3)` : 'none',
                    backgroundColor: sel?navy:'transparent',
                    color: sel?'#fff':dis?'rgba(13,47,68,0.18)':navy,
                    fontFamily:fonts.sans, fontSize:'1.1rem', fontWeight:tod?600:300,
                    cursor:dis?'default':'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    transition:'background 0.12s',
                  }}
                  onMouseEnter={e=>{ if(!dis&&!sel) e.currentTarget.style.backgroundColor='rgba(13,47,68,0.08)'; }}
                  onMouseLeave={e=>{ if(!sel) e.currentTarget.style.backgroundColor='transparent'; }}
                >{d}</button>
              );
            })}
          </div>
          <div style={{ marginTop:'0.7rem', textAlign:'right', borderTop:'1px solid rgba(13,47,68,0.07)', paddingTop:'0.6rem' }}>
            <button type="button"
              onClick={()=>{ if(!minDate||todayDate>=minDate){ onChange(toISO(todayDate)); setOpen(false); } }}
              style={{ background:'none', border:'none', cursor:'pointer', fontFamily:fonts.sans, fontSize:'1.1rem', color:colors.gold }}
            >Today</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Type 3 widget ── */
export default function CheckAvailabilityType3({ room }: { room: CmsRoom }) {
  const router = useRouter();

  const today    = toISO(new Date());
  const tomorrow = toISO(new Date(Date.now() + 86_400_000));

  // Initialize empty so server and client render the same "Select date" placeholder
  // during hydration; fill in the real dates after mount.
  const [checkin,  setCheckin]  = useState('');
  const [checkout, setCheckout] = useState('');

  useEffect(() => {
    setCheckin(toISO(new Date()));
    setCheckout(toISO(new Date(Date.now() + 86_400_000)));
  }, []);
  const [rooms,    setRooms]    = useState(1);

  const [adults,   setAdults]   = useState(1);
  const [children, setChildren] = useState(0);

  const { maxA: maxAdultsPerRoom, maxC: maxChildrenPerRoom } = roomMaxCapacity(room);
  function handleSetAdults(v: number) {
    setAdults(v);
    const needed = neededRooms(v, children, maxAdultsPerRoom, maxChildrenPerRoom);
    if (needed > rooms) setRooms(needed);
  }
  function handleSetChildren(v: number) {
    setChildren(v);
    const needed = neededRooms(adults, v, maxAdultsPerRoom, maxChildrenPerRoom);
    if (needed > rooms) setRooms(needed);
  }
  function handleSetRooms(n: number) {
    setRooms(n);
    const capA = n * maxAdultsPerRoom;
    const capC = n * maxChildrenPerRoom;
    if (adults   > capA) setAdults(capA);
    if (children > capC) setChildren(capC);
  }

  const [loading,    setLoading]    = useState(false);
  const [noRooms,    setNoRooms]    = useState(false);
  const [step,       setStep]       = useState<'form' | 'available'>('form');
  const [bookingUrl, setBookingUrl] = useState('');
  const [booking,    setBooking]    = useState(false);

  const minCheckout = checkin
    ? toISO(new Date(new Date(checkin+'T00:00:00').getTime()+86_400_000))
    : tomorrow;

  function handleCheckinChange(val: string) {
    setCheckin(val);
    setNoRooms(false);
    setStep('form');
    if (checkout <= val)
      setCheckout(toISO(new Date(new Date(val+'T00:00:00').getTime()+86_400_000)));
  }

  function handleReset() {
    setStep('form');
    setNoRooms(false);
    setBookingUrl('');
  }

  const nights = calcNights(checkin, checkout);

  async function handleSubmit() {
    setLoading(true);
    setNoRooms(false);
    try {
      const availableIds = await fetchSandyAvailableIds(checkin, checkout);
      const bypassAvailability = process.env.isByPassAvailablity === 'true' || process.env.NEXT_PUBLIC_IS_BYPASS_AVAILABLITY === 'true';
      if (!bypassAvailability && availableIds.size > 0 && room.external_room_type_id != null) {
        if (!availableIds.has(Number(room.external_room_type_id))) {
          setNoRooms(true);
          return;
        }
      }
      setStep('available');
      /*
      const url = await buildSignedBookingUrl({
        type:     room.slug,
        checkin,
        checkout,
        rooms,
        adults:   Math.max(1, adults),
        children: children > 0 ? children : undefined,
      });
      setBookingUrl(url);
      setStep('available');
      */
    } catch {
      /* If signing fails, fall back to unsigned navigation */
      setStep('available');
      /*
      setBookingUrl(`/book?type=${room.slug}&checkin=${checkin}&checkout=${checkout}&rooms=${rooms}&adults=${adults}`);
      setStep('available');
      */
    } finally {
      setLoading(false);
    }
  }

  async function handleBookNow() {
    router.push('?contact-booking=true', { scroll: false });
    /*
    setBooking(true);
    router.push(bookingUrl);
    // booking stays true — overlay persists until the booking page loads
    */
  }

  /* ── Available result view ───────────────────────────────────── */
  if (step === 'available') {
    const totalPrice = room.base_price * nights;
    const guestLine  = children > 0
      ? `${adults} Adult${adults !== 1 ? 's' : ''} · ${children} Child${children !== 1 ? 'ren' : ''}`
      : `${adults} Adult${adults !== 1 ? 's' : ''}`;
    return (
      <>
      {/* Navigating overlay — shown while router.push is loading the booking page */}
      {booking && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(13, 47, 68, 0.96)',
          backdropFilter: 'blur(10px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: '2rem', padding: '2rem',
        }}>
          <style dangerouslySetInnerHTML={{ __html: '@keyframes spin { to { transform: rotate(360deg); } }' }} />
          <Loader2 size={44} color="rgba(212,175,95,0.9)" strokeWidth={1.4}
            style={{ animation: 'spin 1.1s linear infinite', flexShrink: 0 }} />
          <div style={{ textAlign: 'center', maxWidth: '36rem' }}>
            <p style={{ fontFamily: fonts.serif, fontSize: 'clamp(2rem,4vw,2.6rem)', color: '#fff', fontWeight: 300, letterSpacing: '0.06em', marginBottom: '0.8rem' }}>
              Navigating to booking page
            </p>
            <p style={{ fontFamily: fonts.sans, fontSize: '1.3rem', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Please wait…
            </p>
          </div>
        </div>
      )}
      <div style={{ padding: '2.4rem', borderRadius: '0 0 1.6rem 1.6rem' }}>

        {/* Available badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '1.4rem' }}>
          <CheckCircle2 size={17} color="#1f8d4f" strokeWidth={2} />
          <span style={{ fontFamily: fonts.sans, fontSize: '1.2rem', fontWeight: 700,
            letterSpacing: '0.16em', textTransform: 'uppercase', color: '#1f8d4f' }}>
            Available
          </span>
        </div>

        {/* Summary card */}
        <div style={{ background: 'rgba(13,47,68,0.04)', borderRadius: '0.8rem',
          border: '1px solid rgba(13,47,68,0.09)', padding: '1.4rem 1.6rem', marginBottom: '1.4rem' }}>

          <p style={{ fontFamily: fonts.serif, fontSize: '1.5rem', fontWeight: 400,
            color: navy, marginBottom: '0.9rem', lineHeight: 1.3 }}>
            {room.name}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem',
            fontFamily: fonts.sans, fontSize: '1.2rem', color: 'rgba(13,47,68,0.6)' }}>
            <span>{fmtDisplay(checkin)} → {fmtDisplay(checkout)} · {nights} night{nights !== 1 ? 's' : ''}</span>
            <span>{rooms} Room{rooms !== 1 ? 's' : ''} · {guestLine}</span>
          </div>

          <div style={{ height: '1px', background: 'rgba(13,47,68,0.08)', margin: '1rem 0' }} />

          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontFamily: fonts.sans, fontSize: '1rem', fontWeight: 600,
                letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(13,47,68,0.4)',
                marginBottom: '0.25rem' }}>Total before taxes</p>
              <p style={{ fontFamily: fonts.serif, fontSize: '2.2rem', fontWeight: 400,
                letterSpacing: '-0.03em', color: navy }}>
                ₹{(totalPrice * rooms).toLocaleString('en-IN')}
              </p>
            </div>
            <p style={{ fontFamily: fonts.sans, fontSize: '1.15rem', color: 'rgba(13,47,68,0.45)' }}>
              ₹{room.base_price.toLocaleString('en-IN')}/night
            </p>
          </div>
        </div>

        {/* Book Now */}
        <button
          onClick={handleBookNow}
          disabled={booking}
          className="btn-gold btn-luxury-shine"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
            width: '100%', fontFamily: fonts.sans, fontSize: '1.25rem', fontWeight: 500,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: '#fff', 
            padding: '1.5rem 2rem', border: 'none',
            cursor: booking ? 'wait' : 'pointer', opacity: booking ? 0.75 : 1,
          }}
        >
          {booking ? <><ButtonSpinner color="#fff" size={17} /> Navigating…</> : 'Book Now'}
        </button>

        {/* Change dates */}
        <button type="button" onClick={handleReset}
          style={{ marginTop: '0.9rem', width: '100%', background: 'none', border: 'none',
            fontFamily: fonts.sans, fontSize: '1.2rem', color: 'rgba(13,47,68,0.5)',
            cursor: 'pointer', textAlign: 'center', padding: '0.4rem' }}>
          ← Change dates
        </button>

      </div>
      </>
    );
  }

  /* ── Form view ───────────────────────────────────────────────── */
  return (
    <div style={{ backgroundColor:'inherit', padding:'2.4rem', borderRadius:'0 0 1.6rem 1.6rem' }}>

      {/* Date fields */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.9rem', margin:'0 0 1.2rem' }}>
        <DateField
          label="Check-in" icon={<CalIcon />}
          value={checkin} min={today} onChange={handleCheckinChange}
        />
        <DateField
          label="Check-out" icon={<CalIcon />}
          value={checkout} min={minCheckout} onChange={setCheckout} alignRight
        />
      </div>

      {/* Rooms counter */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
        <p style={labelStyle}>Rooms</p>
        <Counter value={rooms} onChange={handleSetRooms} min={1} max={5} />
      </div>

      {/* Guests */}
      <div style={{ marginBottom:'1.6rem' }}>
        <p style={labelStyle}><PeopleIcon /> Guests</p>
        <GuestsDropdown adults={adults} children={children} onAdults={handleSetAdults} onChildren={handleSetChildren} maxAdults={maxAdultsPerRoom} maxChildren={maxChildrenPerRoom} roomCount={rooms} />
      </div>

      {/* No-rooms feedback */}
      {noRooms && (
        <p style={{
          fontFamily: fonts.sans, fontSize: '1.2rem', color: '#b45309',
          background: 'rgba(180,83,9,0.08)', border: '1px solid rgba(180,83,9,0.2)',
          borderRadius: '0.4rem', padding: '0.75rem 1rem', marginBottom: '1rem',
          textAlign: 'center', lineHeight: 1.5,
        }}>
          This room is not available for those dates. Please try different dates.
        </p>
      )}

      {/* Check Availability */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="btn-primary btn-luxury-shine"
        style={{
          display:'flex', alignItems:'center', justifyContent:'center', gap:'0.6rem',
          width:'100%', textAlign:'center',
          fontFamily:fonts.sans, fontSize:'1.25rem', fontWeight:500,
          letterSpacing:'.18em', textTransform:'uppercase',
          color:'#fff', backgroundColor:colors.navyDeep,
          padding:'1.5rem 2rem', border:'none',
          cursor: loading ? 'wait' : 'pointer',
          opacity: loading ? 0.75 : 1,
        }}
      >
        {loading ? <><ButtonSpinner color="#fff" size={17} /> Checking…</> : 'Check Availability'}
      </button>

      {/* Trust badges */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'1.6rem', marginTop:'1.1rem' }}>
        <span style={{ display:'flex', alignItems:'center', gap:'0.45rem', fontFamily:fonts.sans, fontSize:'1.1rem', color:'rgba(13,47,68,0.5)' }}>
          <ShieldCheck size={13} color="rgba(13,47,68,0.45)" strokeWidth={1.4} />
          Secure booking
        </span>
        <span style={{ width:'1px', height:'1.2rem', background:'rgba(13,47,68,0.12)' }} />
        <span style={{ display:'flex', alignItems:'center', gap:'0.45rem', fontFamily:fonts.sans, fontSize:'1.1rem', color:'rgba(13,47,68,0.5)' }}>
          <Star size={13} color="rgba(13,47,68,0.45)" strokeWidth={1.4} />
          Best price guaranteed
        </span>
      </div>

    </div>
  );
}
